import styles from './GenericConnection.module.css';

import Card, { CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/Card/Card';
import BasicAuthSection from '../authSections/BasicAuthSection';
import OAuthClientCredentialsSection from '../authSections/OAuthClientCredentialsSection';
import OboJwtSection from '../authSections/OboJwtSection';
import SamlBearerSection from '../authSections/SamlBearerSection';
import ApiKeyAuthSection from '../authSections/ApiKeyAuthSection';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackNavigation } from '../../../hooks/useBackNavigation';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '../../../lib/toast';
import { configApi } from '../../../lib/apiClient';
import { useAuthStore } from '../../../store/authStore';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { authFieldsSchema } from '../authSections/types';
import { testConnectionApi } from '../../../lib/testConnection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { Link as LinkIcon, Key } from 'lucide-react';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';


const genericSchema = z.object({
    name: z.string().min(1, 'Connection name is required'),
    environment: z.enum(['test', 'prod', 'dev']).optional(),
    url: z.string().url('Invalid URL'),
    httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).default('GET'),
    authType: z.enum(['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer']),
    testPath: z.string().optional().nullable(),
    ...authFieldsSchema.shape
});

type GenericForm = z.infer<typeof genericSchema>;


export default function GenericConnection() {
    const navigate = useNavigate();
    const goBack = useBackNavigation('/connectors');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [config, setConfig] = useState<any | null>(null);
    const [customHeaders, setCustomHeaders] = useState<Array<{ key: string, value: string }>>([]);
    const user = useAuthStore(state => state.user)
    const { connectors, updateConnection } = useConnectionsStore(state => state);
    const { configId } = useParams();

    const { handleSubmit, setValue, watch, formState: { errors }, setError } = useForm<GenericForm>({
        resolver: zodResolver(genericSchema),
        defaultValues: {
            name: '',
            environment: 'prod' as 'test' | 'prod' | 'dev',
            url: '',
            httpMethod: 'GET' as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
            authType: 'basic',
            testPath: '',
            basic: { username: '', password: '' },
            oauth: { token_url: '', client_id: '', client_secret: '', scope: '' },
            obo: { token_url: '', audience: '', resource: '', issuer_discovery: '' },
            saml: { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' },
            apikey: { key_name: '', key_value: '', in: 'header' },
        },
    });
    const validAuthTypes = ['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer'] as const;
    const authType = watch('authType') || 'basic';

    useEffect(() => {
        if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
            navigate('/');
        }
    }, [user])

    useEffect(() => {
        const init = async () => {
            const configData = connectors.find(c => c.id === configId) || null;
            if (configData) {
                setConfig(configData);
                setValue('name', configData.connectionName || '');
                setValue('environment', (configData.environment as 'test' | 'prod' | 'dev') || 'prod');
                setValue('url', configData.config?.baseUrl || '');
                setValue('httpMethod', (configData.config?.httpMethod as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') || 'GET');
                const headers = Object.entries(configData.defaultHeaders || {})
                    .map(([key, value]) => ({ key, value: value as string }));
                setCustomHeaders(headers);
                setValue(
                    'authType',
                    validAuthTypes.includes(configData.authType as any)
                        ? configData.authType as typeof validAuthTypes[number]
                        : 'basic'
                );
                setValue('basic', configData.authFields?.basic || { username: '', password: '' });
                setValue('oauth', configData.authFields?.oauth || { token_url: '', client_id: '', client_secret: '', scope: '' });
                setValue('obo', configData.authFields?.obo || { token_url: '', audience: '', resource: '', issuer_discovery: '' });
                setValue('saml', configData.authFields?.saml || { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' });
                setValue('apikey', configData.authFields?.apikey || { key_name: '', key_value: '', in: 'header' });
                setValue('testPath', configData.config?.testPath || '');
            }
        };
        init();
    }, [connectors, configId]);

    const onSubmit = async (data: GenericForm) => {
        if (!data.environment) {
            setError('environment', { type: 'manual', message: 'Environment is required' });
            toast.error('Please select an environment');
            return;
        }

        setSaving(true);
        try {
            const rawAuthFields: any = {};
            if (data.authType === 'api_key') {
                rawAuthFields.apikey = data.apikey;
            } else if (data.authType === 'basic') {
                rawAuthFields.basic = data.basic;
            } else if (data.authType === 'oauth2_client_credentials') {
                rawAuthFields.oauth = data.oauth;
            } else if (data.authType === 'oauth2_obo_jwt') {
                rawAuthFields.obo = data.obo;
            } else if (data.authType === 'saml2_bearer') {
                rawAuthFields.saml = data.saml;
            }

            const headersObj = customHeaders.reduce((acc, { key, value }) => {
                if (key.trim() && value.trim()) {
                    acc[key] = value;
                }
                return acc;
            }, {} as Record<string, string>);

            const payload = {
                connectionName: data.name,
                environment: data.environment,
                config: {
                    baseUrl: data.url,
                    httpMethod: data.httpMethod,
                    testPath: data.testPath || '',
                },
                userId: user?.id!,
                authType: data.authType,
                authFields: rawAuthFields,
                defaultHeaders: headersObj,
            };

            let response;
            if (!config) response = await configApi.post(payload);
            else response = await configApi.put(config.id, payload);

            setConfig(response);
            updateConnection(response);
            toast.success('Configuration saved successfully!');
        } catch (error: any) {
            toast.error(`Failed to save configuration: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        if (!config?.id) {
            toast.error('Please save configuration first');
            return;
        }

        const formData = watch();

        const rawAuthFields: any = {};
        if (formData.authType === 'api_key') {
            rawAuthFields.apikey = formData.apikey;
        } else if (formData.authType === 'basic') {
            rawAuthFields.basic = formData.basic;
        } else if (formData.authType === 'oauth2_client_credentials') {
            rawAuthFields.oauth = formData.oauth;
        } else if (formData.authType === 'oauth2_obo_jwt') {
            rawAuthFields.obo = formData.obo;
        } else if (formData.authType === 'saml2_bearer') {
            rawAuthFields.saml = formData.saml;
        }

        const headersObj = customHeaders.reduce((acc, { key, value }) => {
            if (key.trim() && value.trim()) {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string>);

        const connectionData = {
            connectionName: formData.name,
            environment: formData.environment,
            config: {
                baseUrl: formData.url,
                httpMethod: formData.httpMethod,
                testPath: formData.testPath || '',
            },
            authType: formData.authType,
            authFields: rawAuthFields,
            defaultHeaders: headersObj,
        };

        await testConnectionApi(config.id, setTesting, connectionData, user);
    }

    const addHeader = () => {
        setCustomHeaders([...customHeaders, { key: '', value: '' }]);
    }

    const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
        const updated = [...customHeaders];
        updated[index][field] = value;
        setCustomHeaders(updated);
    }

    const removeHeader = (index: number) => {
        setCustomHeaders(customHeaders.filter((_, i) => i !== index));
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>Generic Connection</h1>
                    <p>Configure your generic connection settings</p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        onClick={goBack}
                        className={styles.cancelBtn}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={testConnection}
                        disabled={testing || !config}
                        className={styles.saveBtn}
                    >
                        {testing ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={saving}
                        className={styles.saveBtn}
                    >
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                {/* BASIC SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Basic Settings</CardTitle>
                        <CardDescription>Core endpoint configuration used by all auth types.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="name"
                                label="Connection Name *"
                                value={watch('name') || ''}
                                onChange={(value) => setValue('name', value)}
                                required
                                className={styles.input}
                                externalError={errors.name?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Environment *</label>
                            <select
                                value={watch('environment') || ''}
                                onChange={(e) => {setValue('environment', e.target.value as 'test' | 'prod' | 'dev');
                                    setError('environment', { type: 'manual', message: '' }); }}
                                className={`${styles.select} ${errors.environment ? styles.selectError : ''}`}
                            >
                                <option value="">Select environment</option>
                                <option value="prod">Prod</option>
                                <option value="test">Test</option>
                                <option value="dev">Dev</option>
                            </select>
                            {errors.environment && (
                                <span className={styles.error}>{errors.environment.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="url"
                                label="Base URL *"
                                icon={<LinkIcon size={16} />}
                                value={watch('url') || ''}
                                onChange={(value) => setValue('url', value)}
                                required
                                className={styles.input}
                                externalError={errors.url?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>HTTP Method *</label>
                            <select
                                value={watch('httpMethod')}
                                onChange={(e) => setValue('httpMethod', e.target.value as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE')}
                                className={styles.select}
                            >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="PATCH">PATCH</option>
                                <option value="DELETE">DELETE</option>
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Authentication Type *</label>
                            <select
                                value={watch('authType')}
                                onChange={(e) => {setValue('authType', e.target.value as any);
                                    setError('authType', { type: 'manual', message: '' }); }}
                                className={`${styles.select} ${errors.authType ? styles.selectError : ''}`}
                            >
                                <option value="none">No Authentication</option>
                                <option value="api_key">API Key</option>
                                <optgroup label="Standard">
                                    <option value="oauth2_client_credentials">OAuth2 Client Credentials</option>
                                    <option value="basic">Basic Authentication</option>
                                </optgroup>
                                <optgroup label="Principal Propagation">
                                    <option value="oauth2_obo_jwt">OAuth2 On-Behalf-Of (JWT Bearer)</option>
                                    <option value="saml2_bearer">SAML2 Bearer Assertion</option>
                                </optgroup>
                            </select>
                            {errors.authType && (
                                <span className={styles.error}>{errors.authType.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="text"
                                label="Test Path (optional)"
                                value={watch('testPath') || ''}
                                onChange={(value) => setValue('testPath', value)}
                                placeholder="Relative path for connection testing"
                                className={styles.input}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* API KEY */}
                {authType === 'api_key' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>API Key</CardTitle>
                            <CardDescription>Send an API key as a header or query parameter.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ApiKeyAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* BASIC AUTH */}
                {authType === 'basic' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>Basic Authentication</CardTitle>
                            <CardDescription>Username and password authentication.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <BasicAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* OAUTH2 CLIENT CREDENTIALS */}
                {authType === 'oauth2_client_credentials' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 Client Credentials</CardTitle>
                            <CardDescription>Technical client for calling APIs.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <OAuthClientCredentialsSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* OAUTH2 OBO JWT */}
                {authType === 'oauth2_obo_jwt' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 On-Behalf-Of (JWT Bearer)</CardTitle>
                            <CardDescription>Exchange token for access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <OboJwtSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* SAML2 BEARER */}
                {authType === 'saml2_bearer' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>SAML2 Bearer Assertion</CardTitle>
                            <CardDescription>Exchange a signed SAML assertion for access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <SamlBearerSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* CUSTOM HEADERS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Custom Headers</CardTitle>
                        <CardDescription>Add custom HTTP headers to include with every request.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.fieldGroup}>

                            {customHeaders.map((header, index) => (
                                <div key={index} className={styles.headerRow}>
                                    <SecureInput
                                        fieldType="key"
                                        icon={<Key size={16} />}
                                        value={header.key}
                                        onChange={(value) => updateHeader(index, 'key', value)}
                                        placeholder="Header name"
                                        className={styles.headerInput}
                                    />
                                    <SecureInput
                                        fieldType="text"
                                        value={header.value}
                                        onChange={(value) => updateHeader(index, 'value', value)}
                                        placeholder="Header value"
                                        className={styles.headerInput}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeHeader(index)}
                                        className={styles.removeBtn}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={addHeader}
                                className={styles.addHeaderBtn}
                            >
                                Add Header
                            </button>
                        </div>
                    </CardContent>
                </Card>
            </form>
        </div>
    );
}
